import React from 'react';
import { motion } from 'framer-motion';
import { Cpu, Shield, Activity, Code2, CheckCircle2 } from 'lucide-react';
import Card from '../ui/Card';

const solutionCategories = [
  {
    id: 'ai',
    icon: <Cpu className="w-6 h-6 text-indigoBrand-600 dark:text-indigoBrand-400" />,
    title: 'Artificial Intelligence',
    description: 'Custom machine learning models, retrieval-augmented generation (RAG) pipelines, and computer vision systems designed for low-latency operational environments.',
    technologies: ['Python', 'PyTorch', 'TensorFlow', 'FAISS Vector DB', 'Hugging Face', 'FastAPI'],
    keyCapabilities: [
      'Retrieval-Augmented Generation (RAG)',
      'Multi-spectral media forensics',
      'Cognitive knowledge mastery graphs'
    ]
  },
  {
    id: 'cybersecurity',
    icon: <Shield className="w-6 h-6 text-brand-600 dark:text-brand-400" />,
    title: 'Cybersecurity',
    description: 'Defensive network telemetry, proactive threat anomaly detection, and zero-trust authorization systems built to defend distributed cloud infrastructure.',
    technologies: ['eBPF', 'Rust', 'Go', 'TLS 1.3', 'Network Sandboxing', 'Docker'],
    keyCapabilities: [
      'Real-time behavioral telemetry',
      'Zero-trust access policy enforcement',
      'Automated intrusion containment'
    ]
  },
  {
    id: 'healthcare',
    icon: <Activity className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />,
    title: 'Healthcare Technology',
    description: 'Clinical decision assistance platforms, privacy-preserving digital records, and telemedicine infrastructure that connect patients with healthcare providers.',
    technologies: ['Python', 'PostgreSQL', 'WebRTC', 'React', 'End-to-End Encryption', 'Redis'],
    keyCapabilities: [
      'Verified clinical literature retrieval',
      'Symptom assessment & pre-triage',
      'Secure remote doctor-patient consults'
    ]
  },
  {
    id: 'software-engineering',
    icon: <Code2 className="w-6 h-6 text-amber-600 dark:text-amber-400" />,
    title: 'Software Engineering',
    description: 'Resilient cloud-native microservices, scalable API gateways, and responsive enterprise web and mobile applications engineered for high uptime and longevity.',
    technologies: ['TypeScript', 'React / Next.js', 'Node.js', 'PostgreSQL', 'Kubernetes', 'GraphQL / REST'],
    keyCapabilities: [
      'High-concurrency microservice design',
      'Fault-tolerant data pipelines',
      'Production telemetry and logging'
    ]
  }
];

const Domains = () => {
  return (
    <section id="solutions" className="py-24 relative bg-slate-50/60 dark:bg-slate-900/40 border-t border-slate-200/80 dark:border-slate-800">
      {/* Anchor for domains as well */}
      <span id="domains" className="relative -top-24 block" />
      
      <div className="container mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold uppercase tracking-wider mb-4"
          >
            Capabilities & Focus
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.05 }}
            className="text-3xl md:text-5xl font-display font-bold text-slate-900 dark:text-white mb-4 tracking-tight"
          >
            What We Build
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed"
          >
            We focus our engineering capabilities across four foundational pillars, designing solutions that are robust, verifiable, and immediately practical.
          </motion.p>
        </div>

        {/* 4 Main Solution Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {solutionCategories.map((item, idx) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.08 }}
            >
              <Card className="h-full border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 shadow-subtle hover:shadow-card transition-all duration-300 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200/60 dark:border-slate-700 flex items-center justify-center">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold font-display text-slate-900 dark:text-white">
                        {item.title}
                      </h3>
                      <span className="text-xs font-medium text-slate-400">Core Engineering Practice</span>
                    </div>
                  </div>

                  <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mb-6">
                    {item.description}
                  </p>

                  {/* Key Capabilities */}
                  <div className="mb-6 space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Capabilities</p>
                    {item.keyCapabilities.map((cap, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
                        <CheckCircle2 size={13} className="text-brand-600 dark:text-brand-400 shrink-0" />
                        <span>{cap}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Relevant Technologies */}
                <div className="pt-5 border-t border-slate-100 dark:border-slate-800">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2.5">
                    Technologies
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {item.technologies.map((tech, i) => (
                      <span 
                        key={i}
                        className="px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200/60 dark:border-slate-700"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Domains;
